// contexts/ConfirmDialogContext.tsx
import { createContext, useContext, useState } from "react";

type ConfirmOptions = {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  icon?: "warning" | "info" | "error";
};

type ConfirmDialogContextType = {
  showConfirm: (options: ConfirmOptions) => Promise<boolean>;
};

const ConfirmDialogContext = createContext<
  ConfirmDialogContextType | undefined
>(undefined);

export function useConfirmDialog() {
  const context = useContext(ConfirmDialogContext);
  if (!context)
    throw new Error(
      "useConfirmDialog must be used within ConfirmDialogProvider"
    );
  return context;
}

export function ConfirmDialogProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const [resolveFn, setResolveFn] = useState<(value: boolean) => void>(
    () => () => {}
  );

  const showConfirm = (opts: ConfirmOptions) => {
    setOptions(opts);
    setIsOpen(true);
    return new Promise<boolean>((resolve) => {
      setResolveFn(() => resolve);
    });
  };

  const handleClose = (result: boolean) => {
    setIsOpen(false);
    setTimeout(() => {
      setOptions(null);
      resolveFn(result);
    }, 200);
  };

  return (
    <ConfirmDialogContext.Provider value={{ showConfirm }}>
      {children}
      {isOpen && options && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4">
          <div className="bg-[#161b27] rounded-2xl border border-[#2a3347] max-w-sm w-full p-6">
            <div className="flex items-center gap-2 mb-3">
              {options.icon === "warning" && <span>⚠️</span>}
              {options.icon === "info" && <span>ℹ️</span>}
              {options.icon === "error" && <span>❌</span>}
              <h2 className="text-sm font-semibold text-slate-100">{options.title}</h2>
            </div>
            <p className="text-xs text-slate-400 mb-5">{options.message}</p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => handleClose(false)}
                className="text-xs px-3 py-1.5 rounded-full bg-[#1e2639] text-slate-400 hover:text-slate-200 transition-colors"
              >
                {options.cancelLabel ?? "Abbrechen"}
              </button>
              <button
                onClick={() => handleClose(true)}
                className="text-xs px-3 py-1.5 rounded-full bg-red-600 text-white hover:bg-red-500 transition-colors"
              >
                {options.confirmLabel ?? "OK"}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmDialogContext.Provider>
  );
}
